import './vesselInfoStep.less'

import { Button, Card, Checkbox, Col, Form, Input, Row } from 'antd'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import AutoCompleteSearch from 'components/RobComponents/AutoCompleteSearch'
import { computeTotalHoursOnBoard } from 'pages/RobSurvey/robCommonComputations'
import COUNTRIES from 'constants/countries'
import DateTimeNow from 'components/RobComponents/DateTimeNow'
import i18n from 'domain/i18n/i18n'
import PropTypes from 'prop-types'
import React from 'react'
import RequiredLabel from 'components/RequiredLabel'
import robPermissions from 'constants/robPermissions'
import Text from 'components/Text'
import usePermission from 'hooks/usePermission'

const COUNTRY_LIST = Object.entries(COUNTRIES).map(([code, name]) => ({
  code,
  name,
}))

// Reusable layout wrapper — label left (~35%), input right (~65%)
const Field = ({
  label,
  error,
  children,
  labelCol = { span: 8 },
  wrapperCol = { span: 16 },
}) => (
  <Form.Item
    className="vessel-info-step__field"
    colon={false}
    help={error?.message}
    label={label}
    labelAlign="left"
    labelCol={labelCol}
    validateStatus={error ? 'error' : ''}
    wrapperCol={wrapperCol}
  >
    {children}
  </Form.Item>
)

Field.propTypes = {
  children: PropTypes.node.isRequired,
  error: PropTypes.shape({
    message: PropTypes.string,
  }),
  label: PropTypes.node,
  labelCol: PropTypes.object,
  wrapperCol: PropTypes.object,
}

const VesselInfoStep = ({ onNext }) => {
  const t = key => i18n.t(`rob.survey.general.vesselInfoStep.${key}`)
  const {
    control,
    formState: { errors },
    setValue,
    clearErrors,
  } = useFormContext()
  const can = usePermission()
  const canEdit = can(robPermissions.EDIT_GENERAL)
  const vesselInfoErrors = errors?.general?.vesselInfo ?? {}
  const surveyLogErrors = errors?.general?.surveyLog ?? {}

  const filledByCrew = useWatch({
    control,
    name: 'general.vesselInfo.filledByCrew',
  })

  const surveyorBoardedVessel = useWatch({
    control,
    name: 'general.surveyLog.surveyorBoardedVessel',
  })
  const surveyorLeftVessel = useWatch({
    control,
    name: 'general.surveyLog.surveyorLeftVessel',
  })

  const totalHoursOnBoard = computeTotalHoursOnBoard(
    surveyorBoardedVessel.toString(),
    surveyorLeftVessel.toString()
  )

  console.log('surveyorBoardedVessel = ', surveyorBoardedVessel)

  const SURVEYOR_FIELDS = [
    'surveyCompany',
    'nameOfSurveyor',
    'companyAddress',
    'city',
    'phone',
    'email',
  ]

  const handleFilledByCrewChange = (checked, rhfOnChange) => {
    rhfOnChange(checked)
    if (checked) {
      SURVEYOR_FIELDS.forEach(f => {
        setValue(`general.vesselInfo.${f}`, '', { shouldValidate: false })
        clearErrors(`general.vesselInfo.${f}`)
      })
    }
  }

  return (
    <Row align="top" gutter={16}>
      <Col lg={10} xs={24}>
        <Card
          className="vessel-info-step__card"
          size="small"
          title={t('vesselInformationTitle')}
        >
          <Row>
            <Col xs={24}>
              <Field
                error={vesselInfoErrors.shipName}
                label={
                  <RequiredLabel required text={t('vesselInfo.shipName')} />
                }
              >
                <Controller
                  control={control}
                  name="general.vesselInfo.shipName"
                  render={({ field }) => (
                    <Input
                      {...field}
                      disabled={!canEdit}
                      placeholder={t('vesselInfo.shipNamePlaceholder')}
                    />
                  )}
                />
              </Field>
              <Field
                error={vesselInfoErrors.imoNumber}
                label={
                  <RequiredLabel required text={t('vesselInfo.imoNumber')} />
                }
              >
                <Controller
                  control={control}
                  name="general.vesselInfo.imoNumber"
                  render={({ field }) => (
                    <Input
                      {...field}
                      disabled={!canEdit}
                      maxLength={7}
                      placeholder={t('vesselInfo.imoNumberPlaceholder')}
                    />
                  )}
                />
              </Field>
              <Field
                error={vesselInfoErrors.flag}
                label={<RequiredLabel required text={t('vesselInfo.flag')} />}
              >
                <Controller
                  control={control}
                  name="general.vesselInfo.flag"
                  render={({ field }) => (
                    <AutoCompleteSearch
                      data={COUNTRY_LIST}
                      disabled={!canEdit}
                      onChange={field.onChange}
                      placeholder="e.g. Germany"
                      renderLabel={item => item.name}
                      value={field.value}
                      valueKey="name"
                    />
                  )}
                />
              </Field>
              <Field
                error={vesselInfoErrors.nameOfMaster}
                label={
                  <RequiredLabel required text={t('vesselInfo.nameOfMaster')} />
                }
              >
                <Controller
                  control={control}
                  name="general.vesselInfo.nameOfMaster"
                  render={({ field }) => (
                    <Input
                      {...field}
                      disabled={!canEdit}
                      placeholder={t('vesselInfo.namePlaceholder')}
                    />
                  )}
                />
              </Field>
              <Field
                error={vesselInfoErrors.nameOfChiefEngineer}
                label={
                  <RequiredLabel
                    required
                    text={t('vesselInfo.nameOfChiefEngineer')}
                  />
                }
              >
                <Controller
                  control={control}
                  name="general.vesselInfo.nameOfChiefEngineer"
                  render={({ field }) => (
                    <Input
                      {...field}
                      disabled={!canEdit}
                      placeholder={t('vesselInfo.namePlaceholder')}
                    />
                  )}
                />
              </Field>
              <Field
                error={vesselInfoErrors.voyageNo}
                label={
                  <RequiredLabel required text={t('vesselInfo.voyageNo')} />
                }
              >
                <Controller
                  control={control}
                  name="general.vesselInfo.voyageNo"
                  render={({ field }) => (
                    <Input {...field} disabled={!canEdit} />
                  )}
                />
              </Field>
              <Field
                error={vesselInfoErrors.vesselManagement}
                label={
                  <RequiredLabel
                    required
                    text={t('vesselInfo.vesselManagement')}
                  />
                }
              >
                <Controller
                  control={control}
                  name="general.vesselInfo.vesselManagement"
                  render={({ field }) => (
                    <Input {...field} disabled={!canEdit} />
                  )}
                />
              </Field>
              <Field
                error={vesselInfoErrors.owner}
                label={<RequiredLabel required text={t('vesselInfo.owner')} />}
              >
                <Controller
                  control={control}
                  name="general.vesselInfo.owner"
                  render={({ field }) => (
                    <Input {...field} disabled={!canEdit} />
                  )}
                />
              </Field>
              <Field
                error={vesselInfoErrors.dateOfSurvey}
                label={
                  <RequiredLabel required text={t('vesselInfo.dateOfSurvey')} />
                }
              >
                <Controller
                  control={control}
                  name="general.vesselInfo.dateOfSurvey"
                  render={({ field }) => (
                    <DateTimeNow
                      disabled={!canEdit}
                      onChange={field.onChange}
                      showTime={false}
                      value={field.value}
                    />
                  )}
                />
              </Field>
              <Field
                error={vesselInfoErrors.placeOfSurvey}
                label={
                  <RequiredLabel
                    required
                    text={t('vesselInfo.placeOfSurvey')}
                  />
                }
              >
                <Controller
                  control={control}
                  name="general.vesselInfo.placeOfSurvey"
                  render={({ field }) => (
                    <AutoCompleteSearch
                      disabled={!canEdit}
                      onChange={field.onChange}
                      placeholder={t('vesselInfo.placeOfSurveyPlaceholder')}
                      value={field.value}
                    />
                  )}
                />
              </Field>
              <Field
                error={vesselInfoErrors.terminalName}
                label={
                  <RequiredLabel required text={t('vesselInfo.terminalName')} />
                }
              >
                <Controller
                  control={control}
                  name="general.vesselInfo.terminalName"
                  render={({ field }) => (
                    <Input {...field} disabled={!canEdit} />
                  )}
                />
              </Field>
              <Field
                error={vesselInfoErrors.filledByCrew}
                label={
                  <RequiredLabel required text={t('vesselInfo.filledByCrew')} />
                }
              >
                <Controller
                  control={control}
                  name="general.vesselInfo.filledByCrew"
                  render={({ field }) => (
                    <Checkbox
                      checked={!!field.value}
                      disabled={!canEdit}
                      onChange={e =>
                        handleFilledByCrewChange(
                          e.target.checked,
                          field.onChange
                        )
                      }
                    />
                  )}
                />
              </Field>
              <Field
                error={vesselInfoErrors.surveyCompany}
                label={
                  <RequiredLabel
                    required
                    text={t('vesselInfo.surveyCompany')}
                  />
                }
              >
                <Controller
                  control={control}
                  name="general.vesselInfo.surveyCompany"
                  render={({ field }) => (
                    <Input {...field} disabled={!canEdit || filledByCrew} />
                  )}
                />
              </Field>
              <Field
                error={vesselInfoErrors.nameOfSurveyor}
                label={
                  <RequiredLabel
                    required
                    text={t('vesselInfo.nameOfSurveyor')}
                  />
                }
              >
                <Controller
                  control={control}
                  name="general.vesselInfo.nameOfSurveyor"
                  render={({ field }) => (
                    <Input
                      {...field}
                      disabled={!canEdit || filledByCrew}
                      placeholder={t('vesselInfo.namePlaceholder')}
                    />
                  )}
                />
              </Field>
              <Field
                error={vesselInfoErrors.companyAddress}
                label={t('vesselInfo.companyAddress')}
              >
                <Controller
                  control={control}
                  name="general.vesselInfo.companyAddress"
                  render={({ field }) => (
                    <Input {...field} disabled={!canEdit || filledByCrew} />
                  )}
                />
              </Field>
              <Field error={vesselInfoErrors.city} label={t('vesselInfo.city')}>
                <Controller
                  control={control}
                  name="general.vesselInfo.city"
                  render={({ field }) => (
                    <Input {...field} disabled={!canEdit || filledByCrew} />
                  )}
                />
              </Field>
              <Field
                error={vesselInfoErrors.phone}
                label={t('vesselInfo.phone')}
              >
                <Controller
                  control={control}
                  name="general.vesselInfo.phone"
                  render={({ field }) => (
                    <Input {...field} disabled={!canEdit || filledByCrew} />
                  )}
                />
              </Field>
              <Field
                error={vesselInfoErrors.email}
                label={t('vesselInfo.email')}
              >
                <Controller
                  control={control}
                  name="general.vesselInfo.email"
                  render={({ field }) => (
                    <Input {...field} disabled={!canEdit || filledByCrew} />
                  )}
                />
              </Field>
            </Col>
          </Row>
        </Card>
      </Col>

      <Col lg={14} xs={24}>
        <Card
          className="vessel-info-step__card"
          size="small"
          title={t('surveyLogTitle')}
        >
          <Row>
            <Col xs={24}>
              {[
                {
                  label: t('surveyLog.surveyorLeftBase'),
                  name: 'surveyorLeftBase',
                },
                {
                  label: t('surveyLog.vesselBerthed'),
                  name: 'vesselBerthed',
                },
                {
                  label: t('surveyLog.surveyArrivedBerth'),
                  name: 'surveyArrivedBerth',
                },
                {
                  label: t('surveyLog.surveyorBoardedVessel'),
                  name: 'surveyorBoardedVessel',
                },
                {
                  label: t('surveyLog.surveyCommenced'),
                  name: 'surveyCommenced',
                },
                {
                  label: t('surveyLog.initialMeasurementCompleted'),
                  name: 'initialMeasurementCompleted',
                },
                {
                  label: t('surveyLog.surveyCompleted'),
                  name: 'surveyCompleted',
                },
                {
                  label: t('surveyLog.surveyDocumentCompleted'),
                  name: 'surveyDocumentCompleted',
                },
                {
                  label: t('surveyLog.surveyorLeftVessel'),
                  name: 'surveyorLeftVessel',
                },
                {
                  label: t('surveyLog.surveyorReturnedBase'),
                  name: 'surveyorReturnedBase',
                },
              ].map(({ name, label }) => (
                <Field
                  error={surveyLogErrors[name]}
                  key={name}
                  label={<RequiredLabel required text={label} />}
                >
                  <Controller
                    control={control}
                    name={`general.surveyLog.${name}`}
                    render={({ field }) => (
                      <Col xs={24}>
                        <DateTimeNow
                          disabled={!canEdit}
                          onChange={field.onChange}
                          showTime={true}
                          value={field.value}
                        />
                      </Col>
                    )}
                  />
                </Field>
              ))}
            </Col>
          </Row>
        </Card>
        <Text isBold>{`${t('totalHoursOnBoard')}: ${totalHoursOnBoard}`}</Text>
      </Col>
      <div className="vessel-info-step__footer">
        <Button
          className="vessel-info-step__next-btn"
          onClick={onNext}
          type="primary"
        >
          {t('nextButton')}
        </Button>
      </div>
    </Row>
  )
}

VesselInfoStep.propTypes = {
  onNext: PropTypes.func.isRequired,
}

export default VesselInfoStep
